import { Descriptions, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type AutoplayModuleSpecsFragment } from '../../../../api/fragments/AutoplayModuleSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import AutoplayModuleActions from './AutoplayModuleActions';
import AutoplaySettingsEmptyList from './AutoplaySettingsEmptyList';
import AutoplaySettingsTable from './AutoplaySettingsTable';

export type AutoplayModulePageProps = { module: AutoplayModuleSpecsFragment };

const AutoplayModulePage = ({ module }: AutoplayModulePageProps) => {
    const { t } = useTranslation(['autoplayModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('autoplayModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('autoplayModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<AutoplayModuleActions module={module} />}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('autoplayModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <Divider>{t('autoplayModuleDetails:dividers.autoplaySettings')}</Divider>
            {module.settings.length === 0 ? (
                <AutoplaySettingsEmptyList module={module} />
            ) : (
                <AutoplaySettingsTable module={module} />
            )}
        </ConsolePageWithHeader>
    );
};

export default AutoplayModulePage;
